import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
  createSearchParams,
} from 'react-router';
import qs from 'qs';
import { orderBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import {
  Box,
  Grid2,
  Tooltip,
  Typography,
  TextField,
  Autocomplete,
  Skeleton,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import axios from 'axios';
import config from 'config';
import RemovedAssetIcon from 'components/Icons/removed-asset';
import { handleApiError, parametersApi, referenceDataApi } from 'api';
import {
  emojiValidation,
  formatDate,
  formatRelativeDate,
  intValidation,
  numberValidation,
  stringValidation,
} from 'utils';
import { ParametersContext } from 'context';
import StatusCircle from 'components/status-circle';
import formatParameterInfo from '../../helpers';
import getOptionLabel from '../../helpers/get-option-label';

export default () => {
  const { after, getParameters, updateParametersList, parameters } =
    useContext(ParametersContext);
  const { projectId, assetId } = useParams();
  const [paramSelected, setParamSelected] = useState([]);
  const [paramFiltersActiveCount, setParamFiltersActiveCount] = useState(0);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [isPanelExpanded, setIsPanelExpanded] = useState(true);
  const { search } = useLocation();
  const searchString = { ...qs.parse(search, { ignoreQueryPrefix: true }) };
  const navigate = useNavigate();
  const resetToParamListView = () => {
    navigate({
      pathname: assetId
        ? `/projects/${projectId}/asset/${assetId}/parameters`
        : `/projects/${projectId}/parameters`,

      search: `?${createSearchParams({ ...searchString })}`,
    });
  };
  const [openSnackBar, setOpenSnackBar] = useState({
    open: false,
    variant: '',
  });
  const { enqueueSnackbar } = useSnackbar();
  const [valueOptions, setValueOptions] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [searchSources, setSearchSources] = useState(null);
  const [errors, setErrors] = useState({});
  const [rowsWithErrors, setRowsWithErrors] = useState([]);
  const apiRef = useGridApiRef();
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const unsavedChangesRef = useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });
  const [saveWarningOpen, setSaveWarningOpen] = useState(false);
  const [focusCell, setFocusCell] = useState(null);

  useEffect(() => {
    let timeoutId;
    if (!editLoading && focusCell) {
      const tryFocus = () => {
        const input = document.querySelector(
          `[data-id="${focusCell.id}"] [data-field="${focusCell.field}"] input`
        );
        if (input) {
          input.focus();
          setFocusCell(null); // Reset focusCell after focusing
        } else {
          // Retry after a short delay if input is not found
          timeoutId = setTimeout(tryFocus, 50);
        }
      };
      tryFocus();
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [editLoading, focusCell]);

  useEffect(() => {
    let didCancel = false;
    const source = axios.CancelToken.source();
    const loadAllSources = async after => {
      const query = {
        reference_id: projectId,
        include_global: 'true',
        page_limit: 500,
      };
      if (after) query.after = after;
      if (searchSources) query.search = searchSources;
      const response = await referenceDataApi(
        'getSources',
        query,
        source.token
      ).catch(handleApiError);
      if (!didCancel && response) {
        const { sources, paging } = response;

        setSourceOptions(curr => {
          const sortedByTypeAndTitle = orderBy(
            [...curr, ...sources],
            [res => res.sourceType.name, res => res.title],
            ['asc', 'asc']
          );
          const filterAutomatedProcesses = sortedByTypeAndTitle.filter(
            res => res.sourceType.name !== 'Auto'
          );
          return filterAutomatedProcesses;
        });

        if (paging?.cursors?.after) {
          loadAllSources(paging?.cursors?.after);
        } else setEditLoading(false);
      }
    };
    loadAllSources();
    return () => {
      didCancel = true;
      source.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, setSearchSources]);

  const fetchValueOptions = async row => {
    setEditLoading(true);
    try {
      if (row?.itemTypeId) {
        const { options } = await parametersApi('getOptions', {
          item_type_id: row.itemTypeId,
        });
        setValueOptions(options);
      } else {
        setValueOptions([]);
      }
    } catch (error) {
      setValueOptions([]);
      // eslint-disable-next-line no-console
      console.error('Error fetching value options:', error);
    }
    setEditLoading(false);
  };

  const fetchUnitOptions = async row => {
    setEditLoading(true);
    try {
      if (row?.parameterType?.unitTypeId) {
        const { unitType: response } = await parametersApi('getUnitType', {
          unit_type_id: row.parameterType.unitTypeId,
        }).catch(handleApiError);
        const formatted = response.unitSystems?.map(system => {
          const systemName = system.shortTitle;
          const unitsTypes = system.units.map(unit => ({
            ...unit,
            systemName,
          }));
          return unitsTypes;
        });
        setUnitOptions(formatted ? formatted.flat() : []);
      } else {
        setUnitOptions([]);
      }
    } catch (error) {
      setUnitOptions([]);
      // eslint-disable-next-line no-console
      console.error('Error fetching unit options:', error);
    }
    setEditLoading(false);
  };

  const validateRow = row => {
    const hasUnit = !!row.parameterType?.unitTypeId;
    const currentUnit = row?.selectedEntry?.values?.[0]?.unit;
    const currentValue = row?.selectedEntry?.values?.[0]?.value;
    const currentSource = row?.selectedEntry?.source;
    const dataType = row?.parameterType?.dataType || 'string';
    const currErr = {};

    if (
      hasUnit &&
      !currentUnit &&
      (currentValue || currentValue === false || currentSource)
    ) {
      currErr.unit = 'Unit is required';
    }
    if (
      !currentSource &&
      (currentValue || currentValue === false || currentUnit)
    ) {
      currErr.source = 'Source is required';
    }
    if (
      !currentValue &&
      currentValue !== false &&
      (currentUnit || currentSource)
    ) {
      currErr.value = 'Value is required';
    }
    if (currentValue && !!emojiValidation(currentValue)) {
      currErr.value = 'Value is invalid';
    }
    if (
      currentValue &&
      (dataType === 'float' ||
        dataType === 'integer' ||
        dataType === 'number') &&
      !numberValidation(currentValue)
    ) {
      currErr.value = 'Value is invalid';
    }
    if (
      currentValue &&
      dataType === 'integer' &&
      !intValidation(currentValue)
    ) {
      currErr.value = 'Value is invalid';
    }
    if (
      currentValue &&
      dataType === 'string' &&
      !stringValidation(currentValue)
    ) {
      currErr.value = 'Value is invalid';
    }
    setErrors(curr => ({ ...curr, [row.id]: { ...currErr } }));
  };

  const saveParameterEdits = () => {
    const body = Object.values(unsavedChangesRef.current.unsavedRows).map(
      row => {
        return {
          parameter_id: row.id,
          entry_id: row.selectedEntry?.id || uuidv4(),
          source_id: row.selectedEntry.source.id,
          values: [
            {
              unit_id: row.selectedEntry.values?.[0]?.unit?.id || null,
              value: row.selectedEntry.values[0].value,
            },
          ],
        };
      }
    );

    const updateRevision = async () => {
      try {
        await parametersApi('putEntries', {
          body: {
            entries: [...body],
          },
        });
        setOpenSnackBar({
          open: true,
          variant: 'success',
          message: 'Parameters successfully updated',
        });
        const updatedRows = Object.values(
          unsavedChangesRef.current.unsavedRows
        ).map(row => ({
          ...row,
          rowClass: '',
        }));
        apiRef.current?.updateRows(updatedRows);
        unsavedChangesRef.current = {
          unsavedRows: {},
          rowsBeforeChange: {},
        };
        setHasUnsavedRows(false);
      } catch (err) {
        setOpenSnackBar({
          open: true,
          variant: 'error',
          message: err?.response?.data.details || err.message,
        });
      }
    };
    updateRevision();
    setRowsWithErrors([]);
    setIsSaving(false);
    setSaveWarningOpen(false);
    setErrors({});
  };

  useEffect(() => {
    const errorKeys = Object.keys(errors).filter(
      key => Object.keys(errors[key]).length > 0
    );
    const names = parameters
      .filter(param => errorKeys.includes(param.id))
      .map(param => param.parameterType.name);
    setRowsWithErrors(names);
  }, [errors, parameters]);

  useEffect(() => {
    setSelectedParameters([]);
    setErrors({});
    setRowsWithErrors([]);
    setHasUnsavedRows(false);
    setIsSaving(false);
    setSaveWarningOpen(false);
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };
  }, [assetId, projectId]);

  useEffect(() => {
    if (openSnackBar.open) {
      const title =
        openSnackBar.variant === 'success'
          ? openSnackBar.message
          : `Error: ${openSnackBar.message}`;
      enqueueSnackbar(title, {
        variant: openSnackBar.variant,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSnackBar]);

  const columns = [
    {
      field: 'parameterTypeName',
      flex: 1,
      headerName: 'Parameter type',
      type: 'string',
      valueGetter: (value, row) => {
        return row.parameterType.name;
      },
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                textDecoration: 'underline',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {row.parameterType.name}
            </Typography>
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: 'value',
      flex: 1,
      headerName: 'Value',
      type: 'string',
      renderCell: ({ row }) => {
        const updatedParameter = formatParameterInfo(row);
        let alignment = 'left';
        if (
          row.parameterType.dataType === 'integer' ||
          row.parameterType.dataType === 'float' ||
          row.parameterType.dataType === 'number'
        ) {
          alignment = 'right';
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: alignment === 'right' ? 'flex-end' : 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                textAlign: alignment,
                fontSize: 'inherit',
                fontFamily: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {row.parameterType.dataType === 'date'
                ? formatDate(updatedParameter.parameterValue)
                : updatedParameter.parameterValue}
            </Typography>
          </Box>
        );
      },
      renderEditCell: params => {
        const dataType = params.row.parameterType?.dataType;
        const currentValue =
          params.row.value !== undefined
            ? params.row.value
            : params.row?.selectedEntry?.values?.[0]?.value || '';
        if (editLoading) {
          return <Skeleton sx={{ width: '100%' }} />;
        } else if (dataType === 'date') {
          return (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={enGB}
            >
              <DatePicker
                sx={{ width: '100%' }}
                value={currentValue || null}
                onChange={newValue => {
                  params.api.setEditCellValue({
                    id: params.row.id,
                    field: 'value',
                    value: newValue,
                  });
                }}
                slotProps={{
                  textField: {
                    helperText: errors[params.row.id]?.value || '',
                    error: !!errors[params.row.id]?.value,
                  },
                }}
                required
                minDate={new Date('1000-01-01')}
                maxDate={new Date('2500-01-01')}
              />
            </LocalizationProvider>
          );
        } else if (dataType === 'boolean') {
          return (
            <Autocomplete
              options={[
                { id: true, value: 'True' },
                { id: false, value: 'False' },
              ]}
              sx={{ width: '100%' }}
              value={{ value: currentValue } || null}
              getOptionLabel={option => option.value}
              getOptionValue={option => option.value}
              onChange={(event, newValue) => {
                params.api.setEditCellValue({
                  id: params.row.id,
                  field: 'value',
                  value: newValue.value || '',
                });
              }}
              renderInput={props => (
                <TextField
                  {...props}
                  error={!!errors[params.row.id]?.value}
                  label={errors[params.row.id]?.value || 'Value'}
                />
              )}
            />
          );
        } else if (valueOptions.length) {
          return (
            <Autocomplete
              options={valueOptions}
              sx={{ width: '100%' }}
              value={{ value: currentValue } || null}
              getOptionLabel={option => option.value}
              getOptionValue={option => option.value}
              onChange={(event, newValue) => {
                params.api.setEditCellValue({
                  id: params.row.id,
                  field: 'value',
                  value: newValue.value || '',
                });
              }}
              renderInput={props => (
                <TextField
                  {...props}
                  error={!!errors[params.row.id]?.value}
                  label={errors[params.row.id]?.value || 'Value'}
                />
              )}
            />
          );
        } else
          return (
            <TextField
              error={!!errors[params.row.id]?.value}
              onChange={e => {
                params.api.setEditCellValue({
                  id: params.row.id,
                  field: 'value',
                  value: e.target.value || '',
                });
              }}
              value={currentValue}
              sx={{ width: '100%' }}
              label={errors[params.row.id]?.value || 'Value'}
              name="value"
            />
          );
      },
      sortable: false,
      editable: true,
    },
    {
      field: 'unit',
      flex: 0.5,
      headerName: 'Unit',
      type: 'string',
      renderCell: ({ row }) => {
        const updatedParameter = formatParameterInfo(row);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'elipsis',
            }}
          >
            <Typography
              sx={{
                fontSize: 'inherit',
                fontFamily: 'inherit',
              }}
            >
              {updatedParameter?.units}
            </Typography>
          </Box>
        );
      },
      renderEditCell: params => {
        const currentUnit =
          params.row.unit || params.row.selectedEntry?.values?.[0]?.unit || {};
        if (editLoading) {
          return <Skeleton sx={{ width: '100%' }} />;
        } else
          return (
            <Autocomplete
              options={unitOptions}
              sx={{ width: '100%' }}
              value={currentUnit}
              getOptionValue={unit => unit}
              getOptionLabel={unit => (unit?.symbol ? unit.symbol : '')}
              onChange={(event, newValue) => {
                params.api.setEditCellValue({
                  id: params.row.id,
                  field: 'unit',
                  value: newValue || '',
                });
              }}
              renderInput={props => (
                <TextField
                  {...props}
                  error={!!errors[params.row.id]?.unit}
                  label={errors[params.row.id]?.unit || 'Unit'}
                />
              )}
            />
          );
      },
      sortable: false,
      editable: true,
    },
    {
      field: 'source',
      flex: 1,
      headerName: 'Source',
      type: 'string',
      renderCell: ({ row }) => {
        const updatedParameter = formatParameterInfo(row);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'elipsis',
            }}
          >
            <Typography
              sx={{
                fontSize: 'inherit',
                fontFamily: 'inherit',
              }}
            >
              {updatedParameter?.source?.title}
            </Typography>
          </Box>
        );
      },
      sortable: false,
      editable: true,
      renderEditCell: params => {
        const currentSource =
          params.row.source || params.row.selectedEntry?.source || {};
        if (editLoading) {
          return <Skeleton sx={{ width: '100%' }} />;
        } else
          return (
            <Autocomplete
              options={sourceOptions}
              groupBy={option => option.sourceType.name}
              sx={{ width: '100%' }}
              value={currentSource}
              getOptionValue={source => source}
              getOptionLabel={source => {
                return source.id ? getOptionLabel(source) : '';
              }}
              onChange={(event, newValue) => {
                params.api.setEditCellValue({
                  id: params.row.id,
                  field: 'source',
                  value: newValue || '',
                });
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={e => {
                setSearchSources(e?.target?.value);
              }}
              renderInput={props => (
                <TextField
                  {...props}
                  error={errors[params.row.id]?.source}
                  label={errors[params.row.id]?.source || 'Source'}
                />
              )}
            />
          );
      },
    },
    {
      field: 'lastUpdated',
      flex: 0.5,
      headerName: 'Last Updated',
      type: 'string',
      valueGetter: (value, row) => {
        return formatRelativeDate(row.selectedEntry?.createdAt, undefined);
      },
      sortable: false,
    },
  ];

  if (config.featureFlags.isParameterQAEnabled) {
    columns.push({
      field: 'qa',
      flex: 0.25,
      headerName: 'QA',
      display: 'flex',
      type: 'singleSelect',
      valueOptions: ['check', 'approve', 'reject', 'removed', 'unanswered'],
      renderCell: ({ row }) => {
        return (
          <Grid2 container display="grid" alignItems="center">
            {row.deletedAt && (
              <Tooltip title={'removed'}>
                <RemovedAssetIcon />
              </Tooltip>
            )}
            {!row.deletedAt && (
              <StatusCircle status={row.selectedEntry?.status} />
            )}
          </Grid2>
        );
      },
      sortable: false,
    });
  }

  const hiddenFields = [
    GRID_CHECKBOX_SELECTION_COL_DEF.field,
    // GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field,
  ];
  const getTogglableColumns = () => {
    return columns
      .filter(column => !hiddenFields.includes(column.field))
      .map(column => column.field);
  };

  const updateParameterStatus = async () => {
    const values = selectedParameters.map(param => {
      return {
        revision_id: param?.selectedEntry?.revisionId,
        status:
          param?.selectedEntry?.status === 'answered' ? 'checked' : 'approved',
      };
    });
    try {
      const response = await parametersApi('patchRevisions', {
        body: {
          revisions: [...values],
        },
      });
      if (response) {
        selectedParameters.forEach(param => {
          updateParametersList(
            {
              ...param,
              selectedEntry: {
                ...param.selectedEntry,
                status:
                  param?.selectedEntry?.status === 'answered'
                    ? 'checked'
                    : 'approved',
              },
            },
            param.id
          );
        });
      }
      return response;
    } catch (error) {
      handleApiError(error, []);
      setOpenSnackBar({
        open: true,
        variant: 'error',
        message: error?.response?.data.details || error.message,
      });
    } finally {
      setSelectedParameters([]);
      setParamSelected([]);
    }
  };

  const handleOnRowsScrollEnd = () => {
    if (after) {
      getParameters();
    }
  };
  const handleProcessRowUpdateError = useCallback(error => {
    setOpenSnackBar({
      open: true,
      variant: 'error',
      message: error?.response?.data.details || error.message,
    });
  }, []);

  const discardChanges = useCallback(() => {
    setHasUnsavedRows(false);
    const updatedRows = Object.values(
      unsavedChangesRef.current.rowsBeforeChange
    ).map(row => ({
      ...row,
      rowClass: '', // Reset rowClass to default
    }));

    // Update rows in the DataGrid
    apiRef.current?.updateRows(updatedRows);
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };
    setErrors({});
    setRowsWithErrors({});
  }, [apiRef]);

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    setSaveWarningOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef]);

  const processRowUpdate = useCallback((newRow, oldRow) => {
    const updatedRow = { ...oldRow };
    const rowId = newRow.id;
    let hasChanges = false; // Flag to track if changes were made

    if (
      (newRow.value || newRow.value === false) &&
      (!oldRow?.selectedEntry?.values ||
        newRow.value !== updatedRow?.selectedEntry?.values[0]?.value)
    ) {
      updatedRow.status = 'answered';
      updatedRow.edited = true;
      updatedRow.rowClass = 'row--edited';
      updatedRow.selectedEntry = {
        ...updatedRow?.selectedEntry,
        values: [
          {
            ...(updatedRow?.selectedEntry?.values?.length
              ? updatedRow.selectedEntry?.values[0]
              : {}), // Spread an empty object if values[0] does not exist
            value: newRow.value,
          },
        ],
      };
      hasChanges = true; // Mark changes as true
    }
    if (
      newRow.unit?.symbol &&
      (!oldRow?.selectedEntry?.values ||
        newRow.unit.symbol !==
          updatedRow.selectedEntry?.values[0]?.unit?.symbol)
    ) {
      updatedRow.status = 'answered';
      updatedRow.edited = true;
      updatedRow.rowClass = 'row--edited';
      updatedRow.selectedEntry = {
        ...updatedRow.selectedEntry,
        values: [
          {
            ...(updatedRow?.selectedEntry?.values?.length
              ? updatedRow.selectedEntry?.values[0]
              : {}), // Spread an empty object if values[0] does not exist
            unit: {
              ...newRow.unit,
            },
          },
        ],
      };
      hasChanges = true; // Mark changes as true
    }
    if (
      newRow.source?.id &&
      newRow.source?.id !== updatedRow.selectedEntry?.source?.id
    ) {
      updatedRow.rowClass = 'row--edited';
      updatedRow.status = 'answered';
      updatedRow.selectedEntry = {
        ...updatedRow.selectedEntry,
        source: newRow.source,
      };
      hasChanges = true; // Mark changes as true
    }
    if (hasChanges) {
      validateRow(updatedRow); // Run validation after updating the row
      unsavedChangesRef.current.unsavedRows[rowId] = updatedRow;
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
      }
      setHasUnsavedRows(true);
    }
    return updatedRow;
  }, []);

  return {
    handleOnRowsScrollEnd,
    paramFiltersActiveCount,
    setParamFiltersActiveCount,
    updateParameterStatus,
    columns,
    resetToParamListView,
    paramSelected,
    setParamSelected,
    selectedParameters,
    setSelectedParameters,
    isPanelExpanded,
    setIsPanelExpanded,
    getTogglableColumns,
    openSnackBar,
    valueOptions,
    fetchValueOptions,
    setValueOptions,
    unitOptions,
    fetchUnitOptions,
    setUnitOptions,
    sourceOptions,
    setSourceOptions,
    validateRow,
    errors,
    setErrors,
    rowsWithErrors,
    setRowsWithErrors,
    saveParameterEdits,
    apiRef,
    hasUnsavedRows,
    setHasUnsavedRows,
    isSaving,
    setIsSaving,
    unsavedChangesRef,
    saveWarningOpen,
    setSaveWarningOpen,
    handleProcessRowUpdateError,
    discardChanges,
    saveChanges,
    processRowUpdate,
    focusCell,
    setFocusCell,
  };
};
